import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface MarketAsset {
  symbol: string
  name: string
  type: 'us_stock' | 'hk_stock' | 'crypto' | 'futures'
  currentPrice: number
  change24h: number
  changePercent24h: number
  volume24h: number
  marketCap?: number
  icon?: string
}

export type AssetType = 'us_stocks' | 'hk_stocks' | 'crypto' | 'futures'

interface MarketState {
  assets: MarketAsset[]
  selectedAssetType: AssetType
  loading: boolean
  error: string | null
  searchQuery: string
  sortBy: 'symbol' | 'price' | 'change' | 'volume'
  sortOrder: 'asc' | 'desc'
}

const initialState: MarketState = {
  assets: [],
  selectedAssetType: 'crypto',
  loading: false,
  error: null,
  searchQuery: '',
  sortBy: 'symbol',
  sortOrder: 'asc',
}

const marketSlice = createSlice({
  name: 'market',
  initialState,
  reducers: {
    setAssetType: (state, action: PayloadAction<AssetType>) => {
      state.selectedAssetType = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setSorting: (state, action: PayloadAction<{ sortBy: typeof initialState.sortBy; sortOrder: typeof initialState.sortOrder }>) => {
      state.sortBy = action.payload.sortBy
      state.sortOrder = action.payload.sortOrder
    },
    fetchAssetsStart: (state) => {
      state.loading = true
      state.error = null
    },
    fetchAssetsSuccess: (state, action: PayloadAction<MarketAsset[]>) => {
      state.loading = false
      state.assets = action.payload
      state.error = null
    },
    fetchAssetsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    updateAssetPrice: (state, action: PayloadAction<{ symbol: string; price: number; change: number; volume: number }>) => {
      const asset = state.assets.find(a => a.symbol === action.payload.symbol)
      if (asset) {
        asset.currentPrice = action.payload.price
        asset.change24h = action.payload.change
        asset.changePercent24h = (action.payload.change / (asset.currentPrice - action.payload.change)) * 100
        asset.volume24h = action.payload.volume
      }
    },
  },
})

export const {
  setAssetType,
  setSearchQuery,
  setSorting,
  fetchAssetsStart,
  fetchAssetsSuccess,
  fetchAssetsFailure,
  updateAssetPrice,
} = marketSlice.actions

export default marketSlice.reducer